import React from 'react';
import { FiMessageCircle } from 'react-icons/fi';
import {
  metaAccountStateLabel,
  NOT_CONFIGURED_MESSAGE,
  type MetaAccountStatus,
  type MetaChannel,
  type MetaConnectionStatus,
} from '../../services/MetaConnectionService';

export interface MetaChannelsCardProps {
  /** undefined = ainda consultando · null = a consulta falhou */
  status: MetaConnectionStatus | null | undefined;
  /** Canal cujo login está abrindo (desabilita os botões). */
  connecting: MetaChannel | null;
  disconnecting: boolean;
  onConnect: (channel: MetaChannel) => void;
  onChoosePages: () => void;
  onDisconnect: (channel: MetaChannel) => void;
}

/** O canal está configurado neste ambiente? (backends antigos não mandam por canal: vale o geral) */
export const isMetaChannelConfigured = (status: MetaConnectionStatus, channel: MetaChannel): boolean =>
  (channel === 'instagram' ? status.instagram : status.messenger)?.configured ?? status.configured;

const CHANNELS: Array<{ id: MetaChannel; connect: string; reconnect: string; disconnect: string }> = [
  {
    id: 'instagram',
    connect: 'Conectar Instagram',
    reconnect: 'Reconectar Instagram',
    disconnect: 'Desconectar Instagram',
  },
  {
    id: 'messenger',
    connect: 'Conectar Messenger (Página do Facebook)',
    reconnect: 'Reconectar Messenger',
    disconnect: 'Desconectar Messenger',
  },
];

/**
 * Validade do token de uma conta, para quem quer saber. O do login unificado do Instagram segue a configuração do app na
 * Meta (o normal é não expirar); quando expira, não há renovação automática: reconectar gera outro.
 */
const tokenNote = (a: { tokenNeverExpires?: boolean; tokenExpiresAt?: string | null; channel: MetaChannel }) => {
  if (a.tokenNeverExpires) return 'Acesso sem prazo de validade';
  if (a.channel === 'instagram' && a.tokenExpiresAt) {
    const d = new Date(a.tokenExpiresAt);
    if (!isNaN(d.getTime())) return `Acesso vale até ${d.toLocaleDateString('pt-BR')} (reconecte para renovar)`;
  }
  return null;
};

/**
 * Cartão de Configurações › Conexões da Meta: dois botões independentes, um por canal. Instagram conecta direto pelo login
 * unificado da Meta (entra com Instagram ou Facebook, sem Página; as contas são escolhidas no diálogo da Meta); Messenger passa pelo Facebook Login e pela escolha das Páginas. Cada botão só
 * aparece quando o canal dele está configurado no servidor.
 */
const MetaChannelsCard: React.FC<MetaChannelsCardProps> = ({
  status: ms,
  connecting,
  disconnecting,
  onConnect,
  onChoosePages,
  onDisconnect,
}) => {
  // O WhatsApp oficial tem o próprio cartão (WhatsAppCloudCard): aqui só Instagram e Messenger.
  const accounts = (ms?.accounts ?? []).filter(
    (a): a is MetaAccountStatus & { channel: MetaChannel } => a.channel !== 'whatsapp',
  );
  const hasAccounts = accounts.length > 0;
  const isProblem = (a: { status: string; failedEvents: number }) => a.status !== 'active' || a.failedEvents > 0;
  const hasProblem = accounts.some(isProblem);
  const online = hasAccounts && !hasProblem;
  const statusColor = online ? 'var(--success-color)' : 'var(--error-color)';
  const statusLabel =
    ms === undefined ? 'Verificando…'
      : ms === null ? 'Indisponível'
        : !ms.configured ? 'Não configurado'
          : !hasAccounts ? 'Desconectado'
            : hasProblem ? 'Precisa de atenção' : 'Online';

  return (
    <div className="sector-channel meta-channel">
      <div className="sector-channel-head">
        <h4><FiMessageCircle size={16} /> Instagram e Facebook Messenger</h4>
        <div className="whatsapp-status" style={{ color: statusColor }}>
          <span className="status-dot" style={{ backgroundColor: statusColor }} />
          <span>{statusLabel}</span>
        </div>
      </div>
      {ms && !ms.configured ? (
        <p className="sector-description" role="status">
          {NOT_CONFIGURED_MESSAGE}.{ms.reason ? ` ${ms.reason}` : ''}
        </p>
      ) : null}
      {ms === null ? (
        <p className="sector-description">Não foi possível consultar a conexão com a Meta agora.</p>
      ) : null}
      {hasAccounts ? (
        <ul className="meta-account-list">
          {accounts.map(a => {
            const note = tokenNote(a);
            return (
              <li key={`${a.channel}-${a.accountId}`} className="meta-account-row">
                <span className="meta-account-name">
                  {a.channel === 'instagram'
                    ? `Instagram · @${a.username || a.name || a.accountId}`
                    : `Messenger · ${a.name || a.accountId}`}
                </span>
                <span
                  className="meta-account-state"
                  style={{ color: !isProblem(a) ? 'var(--success-color)' : 'var(--error-color)' }}
                >
                  {metaAccountStateLabel(a.status)}
                </span>
                {a.lastError && isProblem(a) ? <span className="meta-account-error">{a.lastError}</span> : null}
                {a.tokenExpiryWarning && !isProblem(a) ? <span className="meta-account-error">{a.tokenExpiryWarning}</span> : null}
                {note && !isProblem(a) ? <span className="meta-account-token">{note}</span> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {ms && ms.configured ? (
        <div className="whatsapp-buttons-container">
          {CHANNELS.filter(c => isMetaChannelConfigured(ms, c.id)).map(c => {
            const mine = accounts.filter(a => a.channel === c.id);
            const busy = connecting !== null;
            if (mine.length === 0) {
              return (
                <button key={c.id} className="whatsapp-connect-button" disabled={busy} onClick={() => onConnect(c.id)}>
                  {connecting === c.id ? 'Conectando…' : c.connect}
                </button>
              );
            }
            return (
              <React.Fragment key={c.id}>
                {mine.some(isProblem) ? (
                  <button className="whatsapp-connect-button" disabled={busy} onClick={() => onConnect(c.id)}>
                    {connecting === c.id ? 'Conectando…' : c.reconnect}
                  </button>
                ) : null}
                {c.id === 'messenger' ? (
                  <button type="button" className="whatsapp-connect-button is-quiet" onClick={onChoosePages}>
                    Escolher Páginas
                  </button>
                ) : null}
                <button
                  className="whatsapp-connect-button is-danger"
                  onClick={() => onDisconnect(c.id)}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Desconectando…' : c.disconnect}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default MetaChannelsCard;
